import PortalSidebar from "../PortalSidebar/PortalSidebar";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import { format } from "date-fns";

/* ─── Hide these if blank ─── */
const HIDE_IF_BLANK = [
  "_cj_acceptedquote_value",
  "_cj_awardedtocontractor_value",
  "_cj_linkedapprovalrecord_value",
  "_cj_linkedquoteapprovalrecord_value",
];

/* ─── Pagination ─── */
const Pagination = ({ page, total, perPage, onChange }) => {
  const first = (page - 1) * perPage + 1;
  const last = Math.min(first + perPage - 1, total);
  const pages = Math.ceil(total / perPage);
  return (
    <div className={styles.pagination}>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
      >
        Previous
      </button>
      <span className={styles.pageMeta}>
        {first}-{last} of {total}
      </span>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page + 1)}
        disabled={page === pages}
      >
        Next
      </button>
    </div>
  );
};

export default function MaintenanceDetailPage() {
  const { id, buildingId } = useParams();
  const navigate = useNavigate();
  const buildingname = localStorage.getItem("buildingName");
  const [job, setJob] = useState(null);
  const [payments, setPayments] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);


  // pagination
  const perPage = 10;
  const [payPage, setPayPage] = useState(1);
  const [quotePage, setQuotePage] = useState(1);

  // helper to grab formatted or raw
  const display = (obj, key) => {
    const fmt = obj?.[`${key}@OData.Community.Display.V1.FormattedValue`];
    return fmt != null ? fmt : obj?.[key] != null ? obj[key] : "—";
  };
  const fmtDate = (dt) => {
    if (!dt) return "—"; // if blank/null/undefined → hyphen
    try {
      return format(new Date(dt), "dd MMM yyyy");
    } catch {
      return "—";
    }
  };

  /**
   * Conditionally render fields:
   * - only award / linked-approval / all other _value fields become clickable
   * - maintenance & access contacts remain plain text
   * - invoice and quote links as <a>
   */
  const renderField = (key, obj = job) => {
    const raw = obj?.[key];
    const txt = display(obj, key);

    // maintenance & access contacts should NOT be clickable
    if (
      [
        "_cj_maintenancecontactperson_value",
        "_cj_accesscontactperson_value",
      ].includes(key)
    ) {
      return txt;
    }

    // award contractor → /contractor/{id}
    if (key === "_cj_awardedtocontractor_value" && raw) {
      return (
        <span
          className={styles.linkLike}
          onClick={() => navigate(`/contractor/${raw}/${buildingId}`)}
        >
          {txt}
        </span>
      );
    }

    // linked approval record → /building-approval/{id}
    if (key === "_cj_linkedapprovalrecord_value" && raw) {
      return (
        <span
          className={styles.linkLike}
          onClick={() => navigate(`/building-approvals/${raw}`)}
        >
          {txt}
        </span>
      );
    }

    // generic CRM relation → /<entity>/<id>
    if (key.endsWith("_value") && raw) {
      const routeBase = key
        .replace(/^_+/, "")
        .replace(/_value$/, "")
        .replace(/^cj_/, "")
        .replace(/_/g, "-");
      return (
        <span
          className={styles.linkLike}
          onClick={() => navigate(`/${routeBase}/${raw}/${buildingId}`)}
        >
          {txt}
        </span>
      );
    }

    // invoice link
    if (key === "cj_invoicelink" && raw) {
      return (
        <a
          href={raw}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.relatedLinkDark}
        >
          File Link
        </a>
      );
    }

    // quote link → anchor text is the quote's cj_name
    if (key === "cj_quotelink" && raw) {
      return (
        <a href={raw} target="_blank" rel="noopener noreferrer">
          {obj.cj_name}
        </a>
      );
    }

    // fallback plain text
    return txt;
  };

  // Fetch user access...
  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  useEffect(() => {
  const handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    setIsDesktop(desktop);

    // Only force closed when moving to smaller screens.
    // On desktop, keep whatever state the user last chose.
    if (!desktop) {
      setSidebarOpen(false);
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);
  useEffect(() => {
  function handleClick(e) {
    // On desktop/laptop, only the burger should control the sidebar
    if (isDesktop) return;

    const sidebar = document.getElementsByClassName(styles.sidebar)[0];
    const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

    if (!sidebar || !hamburger) return;

    const clickedInsideSidebar = sidebar.contains(e.target);
    const clickedHamburger = hamburger.contains(e.target);

    if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
      setSidebarOpen(false);
    }
  }

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [sidebarOpen, isDesktop]);

  // pull user access for top-right dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        // Set display name safely
        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
            ? name
            : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          }
        );

        const data = await resp.json();
        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);

  // fetch maintenance + payments + quotes
  useEffect(() => {
    document.title = "GMA Portal || Maintenance Detail";
    const token = localStorage.getItem("token");
    if (!token) return navigate("/");
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [jRes, pRes, qRes] = await Promise.all([
          fetch(`https://portal.gmacc.co.za/api/maintenance/${id}/`, {
            headers,
          }),
          fetch(
            `https://portal.gmacc.co.za/api/maintenance/${id}/payments/${buildingId}`,
            { headers }
          ),
          fetch(`https://portal.gmacc.co.za/api/maintenance/${id}/quotes/`, {
            headers,
          }),
        ]);
        if (!jRes.ok || !pRes.ok || !qRes.ok) throw new Error();
        setJob(await jRes.json());
        setPayments((await pRes.json()).value || []);
        setQuotes((await qRes.json()).value || []);
      } catch {
        setError("Could not load maintenance job.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <main className={styles.contentArea}>
            <div className={styles.panel}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }
  if (error) {
    return <p className={styles.errorText}>{error}</p>;
  }

  // which tabs to show
  const hasContractor = Boolean(job?._cj_awardedtocontractor_value);

  // layout definitions
  const summaryFields = [
    ["Job Number", "cj_maintenanacenumber"],
    ["Job Name", "cj_name"],
    ["Type", "cj_maintenanacetype"],
    ["Completed On", "cj_dateofjobcompletion"],
  ];
  const contacts = [
    ["Maintenance Contact", "_cj_maintenancecontactperson_value"],
    ["Access Contact", "_cj_accesscontactperson_value"],
    ["Instruction", "cj_instruction"],
    ["Description", "cj_fulldescription"],
    ["Requested By", "cj_requestedby"],
    ["Invoice Link", "cj_invoicelink"],
  ];
  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>
            {buildingname} – {display(job, "cj_name")}
          </h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>

      {/* CONTENT */}
      <div className={styles.shell}>
        <PortalSidebar
  activePortal="trustee"
  activeTab="buildings"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  ticketRole="trustee"
/>
        {/* SUMMARY */}
        <main className={styles.contentArea}>
          <div className={styles.panel}>
            <div className={styles.paymentHeaderCard}>
              <div className={styles.card}>
                <button
                  className={styles.paymentBackBtn}
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>

                {/* TITLE */}
                <h1 className={styles.paymentHeaderTitle}>Job Details</h1>

                {/* DIVIDER */}
                <div className={styles.paymentHeaderDivider} />

                {/* LABEL ROW (AUTO GRID) */}
                <div
                  className={styles.paymentHeaderGridAuto}
                  style={{ "--col-count": summaryFields.length }}
                >
                  {summaryFields.map(([lbl]) => (
                    <div key={lbl} className={styles.paymentHeaderLabelItem}>
                      {lbl}:
                    </div>
                  ))}
                </div>

                {/* VALUE PILL (AUTO GRID) */}
                <div className={styles.paymentHeaderValuePill}>
                  <div
                    className={styles.paymentHeaderGridAuto}
                    style={{ "--col-count": summaryFields.length }}
                  >
                    {summaryFields.map(([, k]) => (
                      <div key={k} className={styles.paymentHeaderValueItem}>
                        {k.toLowerCase().includes("date")
                          ? fmtDate(job[k])
                          : display(job, k)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* TABS */}
            <div className={styles.subTabs}>
              <div
                className={
                  activeTab === "general" ? styles.subTabActive : styles.subTab
                }
                onClick={() => {
                  setActiveTab("general");
                  setQuotePage(1);
                }}
              >
                General
              </div>

              {hasContractor && (
                <div
                  className={
                    activeTab === "appointment"
                      ? styles.subTabActive
                      : styles.subTab
                  }
                  onClick={() => {
                    setActiveTab("appointment");
                    setPayPage(1);
                  }}
                >
                  Appointment
                </div>
              )}
            </div>

            {/* GENERAL */}
            {activeTab === "general" && (
              <>
                <div className={styles.paymentCardGrid}>
                  {/* General */}
                  <div className={styles.paymentCardDark}>
                    <h2 className={styles.cardSectionTitle}>General</h2>

                    <div style={{ marginTop: 12 }} />

                    {contacts.map(([lbl, k]) => (
                      <div key={k} className={styles.inputGroup}>
                        <h3 className={styles.inputLabel}>{lbl}:</h3>

                        <div className={styles.inputField}>
                          {renderField(k)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* RELATED RECORDS*/}
                  <div className={styles.paymentCard}>
                    <h2 className={styles.cardSectionTitleLight}>
                      Related Records
                    </h2>

                    <div style={{ marginTop: 12 }} />

                    {[
                      ["Accepted Quote", "_cj_acceptedquote_value"],
                      [
                        "Awarded To Contractor",
                        "_cj_awardedtocontractor_value",
                      ],
                      [
                        "Work Satisfaction Approval Record",
                        "_cj_linkedapprovalrecord_value",
                      ],
                      [
                        "Quote Approval Record",
                        "_cj_linkedquoteapprovalrecord_value",
                      ],
                    ]
                      .filter(([, k]) => job?.[k])
                      .map(([lbl, k]) => (
                        <div key={k} className={styles.inputGroupLight}>
                          <h3 className={styles.inputLabelLight}>{lbl}:</h3>

                          <div
                            className={`${styles.inputFieldLight} ${styles.relatedLink}`}
                          >
                            {renderField(k)}
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* STATUS*/}
                  <div className={styles.paymentCard}>
                    <h2 className={styles.cardSectionTitleLight}>Status</h2>

                    <div style={{ marginTop: 12 }} />

                    {[
                      ["Progress", "cj_progress"],
                      ["Trustee Quote Approval", "cj_trusteequoteapproval"],
                      ["Trustee Work Approval", "cj_trusteeworkapproval"],
                    ].map(([lbl, k]) => (
                      <div key={k} className={styles.inputGroupLight}>
                        <h3 className={styles.inputLabelLight}>{lbl}:</h3>

                        <div className={styles.inputFieldLight}>
                          {display(job, k)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quotes (paginated) */}
                <div
                  className={styles.paymentCard}
                  style={{ marginTop: 20 }}
                  data-card="active-quotes"
                >
                  <h2>Active Quotes</h2>
                  <div style={{ marginTop: 12 }} />
                  {quotes.length === 0 ? (
                    <p className={styles.infoText}>No quotes available.</p>
                  ) : (
                    <>
                      <div className={styles.responsiveTableWrapper}>
                        <table className={styles.dataTable}>
                          <thead>
                            <tr>
                              <th>Quote</th>
                              <th>Contractor</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotes
                              .slice(
                                (quotePage - 1) * perPage,
                                quotePage * perPage
                              )
                              .map((q) => (
                                <tr key={q.cj_quoteid}>
                                  <td>{renderField("cj_quotelink", q)}</td>
                                  <td>
                                    {renderField("_cj_contractor_value", q)}
                                  </td>
                                  <td>{display(q, "cj_amount")}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      <Pagination
                        page={quotePage}
                        total={quotes.length}
                        perPage={perPage}
                        onChange={setQuotePage}
                      />
                    </>
                  )}
                </div>
              </>
            )}

            {/* APPOINTMENT */}
            {activeTab === "appointment" && (
              <div className={styles.paymentCardGrid}>
                {/* Appointment */}
                <div className={styles.paymentCardDark}>
                  <h2 className={styles.cardSectionTitle}>Appointment</h2>

                  <div style={{ marginTop: 12 }} />

                  {[
                    [
                      "Maintenance Contact",
                      "_cj_maintenancecontactperson_value",
                    ],
                    ["Access Contact", "_cj_accesscontactperson_value"],
                    ["Contractor", "_cj_awardedtocontractor_value"],
                    ["Accepted Quote", "_cj_acceptedquote_value"],
                    ["Date Quote Accepted", "cj_datewhenquoteaccepted"],
                    ["Invoice Link", "cj_invoicelink"],
                    [
                      "Additional Details",
                      "cj_additionaldetailsregardingcommunication",
                    ],
                    ["Estimated Completion", "cj_dateofjobcompletion"],
                    ["Invoice Amount", "cj_invoiceamount"],
                    ["Paid Amount", "cj_sumofpayments"],
                    ["Amount To Still Be Paid", "cr446_remainderamount1"],
                  ]
                    .filter(([_, k]) =>
                      HIDE_IF_BLANK.includes(k) ? Boolean(job?.[k]) : true
                    )
                    .map(([lbl, k]) => (
                      <div key={k} className={styles.inputGroup}>
                        <h3 className={styles.inputLabel}>{lbl}:</h3>

                        {/* Special fields use relatedLinkDark inside the inputField pill */}
                        {[
                          "_cj_awardedtocontractor_value",
                          "_cj_acceptedquote_value",
                        ].includes(k) ? (
                          <div className={styles.inputField}>
                            {React.cloneElement(renderField(k), {
                              className: styles.relatedLinkDark,
                            })}
                          </div>
                        ) : (
                          <div className={styles.inputField}>
                            {renderField(k)}
                          </div>
                        )}
                      </div>
                    ))}
                </div>

                {/* Payments (paginated) */}
                <div className={styles.paymentCard}>
                  <h2>Payments</h2>
                  <div style={{ marginTop: 12 }} />
                  <div className={styles.responsiveTableWrapper}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>Payment Number</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments
                          .slice((payPage - 1) * perPage, payPage * perPage)
                          .map((p) => (
                            <tr
                              key={p.cr446_paymentid}
                              onClick={() =>
                                navigate(
                                  `/payments/${p.cr446_paymentid}/${buildingId}`
                                )
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <td className="paymentNumberCell">
                                {p.cr446_paymentpk}
                              </td>
                              <td className="amountCell">
                                <span className="currency">R</span>
                                <span className="amountValue">
                                  {display(p, "cr446_amount")}
                                </span>
                              </td>
                              <td>{display(p, "cj_progress")}</td>
                              <td>
                                {fmtDate(p.cj_paymenttime || p.createdon)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={payPage}
                    total={payments.length}
                    perPage={perPage}
                    onChange={setPayPage}
                  />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
